import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { seoAPI } from '../../services/api';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface AiTagCheck {
  name: string;
  key: string;
  present: boolean;
  current_value?: string;
  suggested_value?: string;
  importance: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  category: 'meta' | 'og' | 'schema' | 'ai_bot' | 'structured';
}

interface AiScanResult {
  url: string;
  scanned_at: string;
  overall_score: number;
  checks: AiTagCheck[];
  llms_txt_exists?: boolean;
  robots_blocks_ai?: boolean;
  ai_crawlers_blocked?: string[];
  summary?: string;
}

const impColor = (imp: AiTagCheck['importance']) =>
  imp === 'critical' ? '#ef4444' : imp === 'high' ? '#f59e0b' : imp === 'medium' ? '#3b82f6' : '#10b981';

const catIcon = (cat: AiTagCheck['category']) => {
  const map: Record<string, string> = {
    meta: 'bi-card-text', og: 'bi-share-fill', schema: 'bi-braces',
    ai_bot: 'bi-robot', structured: 'bi-diagram-3-fill',
  };
  return map[cat] || 'bi-check-circle';
};

const SeoForAIPage: React.FC = () => {
  useDocumentTitle('SEO for AI');
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;

  const [result, setResult] = useState<AiScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [filterCat, setFilterCat] = useState<string>('all');
  const [showMissing, setShowMissing] = useState(false);

  useEffect(() => {
    if (assetId) loadResult();
  }, [assetId]);

  const loadResult = async () => {
    if (!assetId) return;
    try {
      const r = await seoAPI.getAITagsResult(assetId);
      if (r.data?.data) setResult(r.data.data);
    } catch { /* no prior scan */ }
  };

  const handleScan = async () => {
    if (!assetId || loading) return;
    setLoading(true);
    try {
      const r = await seoAPI.scanAITags(assetId);
      setResult(r.data?.data || null);
      toast.success('AI tag scan complete');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (check: AiTagCheck) => {
    if (!assetId || generating) return;
    setGenerating(check.key);
    try {
      const r = await seoAPI.generateAITag(assetId, check.key);
      const suggested = r.data?.data?.suggested_value;
      if (suggested && result) {
        setResult(prev => prev ? {
          ...prev,
          checks: prev.checks.map(c => c.key === check.key ? { ...c, suggested_value: suggested } : c),
        } : prev);
      }
      toast.success('Tag generated by AI');
    } catch {
      toast.error('Failed to generate tag');
    } finally {
      setGenerating(null);
    }
  };

  const categories = ['all', 'meta', 'og', 'schema', 'ai_bot', 'structured'];
  const catLabel: Record<string, string> = {
    all: 'All', meta: 'Meta Tags', og: 'Open Graph', schema: 'Schema.org',
    ai_bot: 'AI Bots', structured: 'Structured Data',
  };

  const filteredChecks = (result?.checks || []).filter(c => {
    if (showMissing && c.present) return false;
    if (filterCat !== 'all' && c.category !== filterCat) return false;
    return true;
  });

  const scoreColor = (s: number) => s >= 80 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444';
  const scoreLabel = (s: number) => s >= 80 ? 'AI-Ready' : s >= 50 ? 'Needs Work' : 'Not Optimized';

  return (
    <DashboardLayout>
      <div className="container-fluid py-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
          <div>
            <h4 className="fw-bold mb-1">
              <i className="bi bi-robot text-primary me-2"></i>SEO for AI
              <span className="badge bg-primary ms-2" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>NEW</span>
            </h4>
            <p className="text-muted small mb-0">
              Optimise your website for AI search engines — ChatGPT, Perplexity, Claude, Gemini &amp; Google SGE
            </p>
          </div>
          <button className={`btn btn-primary ${loading ? 'disabled' : ''}`} onClick={handleScan} disabled={loading}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-1" />Scanning…</>
              : <><i className="bi bi-search me-1"></i>{result ? 'Re-scan' : 'Scan Now'}</>
            }
          </button>
        </div>

        {/* What is this */}
        {!result && !loading && (
          <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #eff6ff, #f0fdf4)' }}>
            <div className="card-body p-4">
              <h5 className="fw-bold mb-3"><i className="bi bi-stars me-2 text-primary"></i>Why optimise for AI?</h5>
              <div className="row g-3">
                {[
                  { icon: 'bi-chat-dots-fill', color: '#6366f1', title: 'AI Answers', desc: 'ChatGPT, Perplexity & Claude now answer queries by reading your website. Wrong tags = wrong answers.' },
                  { icon: 'bi-google', color: '#ef4444', title: 'Google SGE', desc: 'Google\'s AI search summaries pull from structured data and clear meta descriptions.' },
                  { icon: 'bi-shield-check', color: '#10b981', title: 'Bot Policies', desc: 'Control which AI crawlers can index your site with robots.txt & llms.txt.' },
                  { icon: 'bi-diagram-3', color: '#f59e0b', title: 'Schema Markup', desc: 'JSON-LD schema helps AI models understand your content type, author & date.' },
                ].map((item, i) => (
                  <div key={i} className="col-12 col-sm-6">
                    <div className="d-flex gap-3">
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: item.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: 18 }}></i>
                      </div>
                      <div>
                        <div className="fw-semibold small">{item.title}</div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{item.desc}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-center">
                <button className="btn btn-primary btn-lg px-5" onClick={handleScan} disabled={loading}>
                  <i className="bi bi-search me-2"></i>Start Free AI SEO Scan
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status"></div>
              <p className="text-muted mb-0">Scanning your website for AI optimisation…</p>
              <small className="text-muted">Checking meta tags, structured data, Open Graph &amp; AI bot policies</small>
            </div>
          </div>
        )}

        {result && !loading && (
          <>
            {/* Score card */}
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center py-4">
                    <div style={{ fontSize: '3rem', fontWeight: 900, color: scoreColor(result.overall_score), lineHeight: 1 }}>
                      {result.overall_score}
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: scoreColor(result.overall_score) }}>
                      {scoreLabel(result.overall_score)}
                    </div>
                    <div className="text-muted small mt-1">AI Readiness Score</div>
                    <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', marginTop: 10 }}>
                      <div style={{ height: '100%', width: `${result.overall_score}%`, background: scoreColor(result.overall_score), borderRadius: 4, transition: 'width 1s ease' }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center py-4">
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: result.checks.filter(c => c.present).length > result.checks.length * 0.7 ? '#10b981' : '#f59e0b' }}>
                      {result.checks.filter(c => c.present).length}/{result.checks.length}
                    </div>
                    <div className="text-muted small">Tags Present</div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden', marginTop: 8 }}>
                      <div style={{ height: '100%', width: `${(result.checks.filter(c => c.present).length / result.checks.length) * 100}%`, background: '#10b981', borderRadius: 3 }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className={`card border-0 shadow-sm h-100 ${result.llms_txt_exists ? 'bg-success bg-opacity-10' : ''}`}>
                  <div className="card-body text-center py-4">
                    <i className={`bi ${result.llms_txt_exists ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}
                      style={{ fontSize: 32, color: result.llms_txt_exists ? '#10b981' : '#94a3b8' }}></i>
                    <div className="fw-semibold small mt-2">llms.txt</div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      {result.llms_txt_exists ? 'Found — AI crawlers can index' : 'Missing — create /llms.txt'}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className={`card border-0 shadow-sm h-100 ${result.robots_blocks_ai ? 'bg-warning bg-opacity-10' : ''}`}>
                  <div className="card-body text-center py-4">
                    <i className={`bi ${result.robots_blocks_ai ? 'bi-shield-exclamation' : 'bi-shield-check'}`}
                      style={{ fontSize: 32, color: result.robots_blocks_ai ? '#f59e0b' : '#10b981' }}></i>
                    <div className="fw-semibold small mt-2">AI Bot Policy</div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      {result.robots_blocks_ai
                        ? `Blocking: ${(result.ai_crawlers_blocked || []).join(', ') || 'some bots'}`
                        : 'AI crawlers allowed'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            {result.summary && (
              <div className="alert alert-primary border-0 d-flex gap-3 align-items-start mb-4">
                <i className="bi bi-stars fs-5 mt-1 flex-shrink-0"></i>
                <div>
                  <strong>AI Analysis:</strong> {result.summary}
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body py-2 d-flex align-items-center gap-3 flex-wrap">
                <div className="btn-group btn-group-sm">
                  {categories.map(cat => (
                    <button key={cat} className={`btn ${filterCat === cat ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setFilterCat(cat)}>
                      {catLabel[cat]}
                    </button>
                  ))}
                </div>
                <div className="form-check ms-auto mb-0">
                  <input type="checkbox" className="form-check-input" id="showMissing"
                    checked={showMissing} onChange={e => setShowMissing(e.target.checked)} />
                  <label className="form-check-label small" htmlFor="showMissing">Show missing only</label>
                </div>
                <small className="text-muted">
                  Scanned: {new Date(result.scanned_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </small>
              </div>
            </div>

            {/* Checks list */}
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                {filteredChecks.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-check-circle-fill text-success fs-3 d-block mb-2"></i>
                    All checks pass for this filter!
                  </div>
                ) : filteredChecks.map((check, i) => (
                  <div key={i} style={{
                    borderBottom: '1px solid #f1f5f9',
                    background: check.present ? '#fff' : '#fffbf0',
                    padding: '14px 20px',
                  }}>
                    <div className="d-flex align-items-start gap-3">
                      {/* Status icon */}
                      <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginTop: 2,
                        background: check.present ? '#d1fae515' : '#fef3c720',
                        border: `1.5px solid ${check.present ? '#10b981' : impColor(check.importance)}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className={`bi ${check.present ? 'bi-check-lg' : catIcon(check.category)}`}
                          style={{ fontSize: 13, color: check.present ? '#10b981' : impColor(check.importance) }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>{check.name}</span>
                          <span style={{
                            fontSize: '0.62rem', fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                            background: check.present ? '#d1fae5' : impColor(check.importance) + '15',
                            color: check.present ? '#065f46' : impColor(check.importance),
                          }}>
                            {check.present ? '✓ Present' : check.importance.toUpperCase()}
                          </span>
                          <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{catLabel[check.category] || check.category}</span>
                        </div>
                        <p className="text-muted mb-1" style={{ fontSize: '0.78rem' }}>{check.description}</p>

                        {/* Current value */}
                        {check.current_value && (
                          <div className="mb-1">
                            <span className="badge bg-light text-dark border me-1" style={{ fontSize: '0.68rem' }}>Current</span>
                            <code style={{ fontSize: '0.72rem', color: '#475569', wordBreak: 'break-all' }}>{check.current_value.slice(0, 200)}{check.current_value.length > 200 ? '…' : ''}</code>
                          </div>
                        )}

                        {/* Suggested value */}
                        {check.suggested_value && (
                          <div className="p-2 rounded mb-2" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <span className="badge bg-success me-1" style={{ fontSize: '0.65rem' }}>AI Suggestion</span>
                            <code style={{ fontSize: '0.72rem', color: '#15803d', wordBreak: 'break-all' }}>{check.suggested_value}</code>
                            <button className="btn btn-sm btn-link p-0 ms-2" style={{ fontSize: '0.72rem', color: '#15803d' }}
                              onClick={() => { navigator.clipboard.writeText(check.suggested_value!); toast.success('Copied!'); }}>
                              <i className="bi bi-clipboard me-1"></i>Copy
                            </button>
                          </div>
                        )}

                        {/* Generate button for missing tags */}
                        {!check.present && !check.suggested_value && (
                          <button
                            className={`btn btn-sm btn-outline-primary mt-1 ${generating === check.key ? 'disabled' : ''}`}
                            onClick={() => handleGenerate(check)} disabled={generating === check.key}>
                            {generating === check.key
                              ? <><span className="spinner-border spinner-border-sm me-1" />Generating…</>
                              : <><i className="bi bi-stars me-1"></i>Generate with AI</>
                            }
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SeoForAIPage;
